import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {AbortController | undefined} */
  #countRequest;

  /** @type {number | undefined} */
  #productCount;

  #countObserver = new MutationObserver(() => this.#renderProductCount());

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleCountFilterUpdate);
    if (this.dataset.countSection) {
      // Section morphs replace labels inside nested facet components. Reapply
      // the current count even if its request finishes before the morph does.
      this.#countObserver.observe(this, { childList: true, subtree: true });
    }
    this.#updateProductCount();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleCountFilterUpdate);
    this.#countObserver.disconnect();
    this.#countRequest?.abort();
  }

  // Labels live inside nested facet components, outside this component's refs.
  #countLabels() {
    return this.querySelectorAll('[data-collection-count-label]');
  }

  #handleCountFilterUpdate = () => this.#updateProductCount();

  #renderProductCount() {
    // The theme's locale files use the same text for every non-one form.
    const form = this.#productCount !== undefined &&
      new Intl.PluralRules(document.documentElement.lang || 'en').select(this.#productCount) === 'one'
      ? 'one' : 'other';
    for (const label of this.#countLabels()) {
      if (!(label instanceof HTMLElement)) continue;
      const text = this.#productCount === undefined
        ? label.dataset.fallback ?? ''
        : label.dataset[form]?.replaceAll('[count]', String(this.#productCount)) ?? '';
      if (label.textContent !== text) label.textContent = text;
    }
  }

  async #updateProductCount() {
    this.#countRequest?.abort();
    this.#productCount = undefined;
    this.#renderProductCount();
    const section = this.dataset.countSection;
    if (!section || !this.#countLabels().length) return;

    const controller = new AbortController();
    this.#countRequest = controller;
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.delete('sections');
    url.searchParams.set('section_id', section);

    let total = 0;
    let pages = 1;
    let rawTotal;
    try {
      for (let page = 1; page <= pages; page++) {
        // Always start at page 1, including a direct visit to a later grid page.
        url.searchParams.set('page', String(page));
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Collection count request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const result = html.querySelector('[data-collection-count-page]');
        if (!(result instanceof HTMLTemplateElement)) throw new Error('Collection count response missing');
        const currentPage = Number(result.dataset.collectionCountPage);
        const pageCount = Number(result.dataset.pages);
        const itemCount = Number(result.dataset.total);
        const visibleCount = Number(result.dataset.count);
        if (
          ![currentPage, pageCount, itemCount, visibleCount].every(Number.isSafeInteger) ||
          currentPage !== page || itemCount < 0 || itemCount > 25000 ||
          pageCount < 0 || Math.max(1, pageCount) !== Math.max(1, Math.ceil(itemCount / 250)) ||
          visibleCount < 0 || visibleCount > Math.min(250, itemCount - (page - 1) * 250) ||
          (rawTotal !== undefined && itemCount !== rawTotal)
        ) throw new Error('Collection count response incomplete');
        rawTotal = itemCount;
        pages = Math.max(1, pageCount);
        total += visibleCount;
      }
      if (controller.signal.aborted || !this.isConnected) return;

      this.#productCount = total;
      this.#renderProductCount();
    } catch (error) {
      // A failed/incomplete count must never fall back to the raw product total.
      if (!controller.signal.aborted) console.error(error);
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
    this.syncFilledRows();
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
